import { fetchWithAuth } from './api';
import { normalizePhotoForUpload } from '../utils/uploadPhoto';

export interface NormaInterna {
  id: string;
  customer_id: string;
  title: string;
  content: string;
  order: number;
  is_active: boolean;
  created_by_id?: string;
  created_at: string;
  updated_at: string;
  // Attached PDF (the condo's regimento interno / convenção). `document_url` arrives already
  // PRESIGNED and short-lived (~15 min) — it is not a stable link, so it must not be stored or
  // shared; re-list to get a fresh one.
  document_url?: string;
  document_name?: string;
  document_size?: number;
  document_type?: string;
}

export interface CreateNormaPayload {
  title: string;
  content: string;
  order?: number;
}

export interface UpdateNormaPayload {
  title?: string;
  content?: string;
  order?: number;
}

export const normasService = {
  async listNormas(customerId: string): Promise<NormaInterna[]> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/normas`);
    if (!res.ok) throw new Error(`Failed to fetch normas: ${res.status}`);
    const data = await res.json();
    return data.normas ?? [];
  },

  async createNorma(customerId: string, payload: CreateNormaPayload): Promise<NormaInterna> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/normas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Failed to create norma: ${res.status}`);
    const data = await res.json();
    return data.norma;
  },

  async updateNorma(customerId: string, normaId: string, payload: UpdateNormaPayload): Promise<NormaInterna> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/normas/${normaId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Failed to update norma: ${res.status}`);
    const data = await res.json();
    return data.norma;
  },

  async deleteNorma(customerId: string, normaId: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/normas/${normaId}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(`Failed to delete norma: ${res.status}`);
  },

  /**
   * Attaches (or replaces) the norma's PDF. Deliberately NOT setting Content-Type: the browser
   * must generate the multipart boundary itself, and naming the type by hand omits it, which makes
   * the server fail to parse the body.
   */
  async uploadDocument(
    customerId: string,
    normaId: string,
    file: File,
  ): Promise<{ document_url: string; document_name: string; document_size: number }> {
    const body = new FormData();
    body.append('file', await normalizePhotoForUpload(file));
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/normas/${normaId}/document`, {
      method: 'POST',
      body,
    });
    if (!res.ok) {
      // The server explains size/type rejections in `error`; surfacing that beats a status code.
      let message = `Failed to upload document: ${res.status}`;
      try {
        const data = await res.json();
        if (data?.error) message = data.error;
      } catch {
        /* non-JSON response — keep the status message */
      }
      throw new Error(message);
    }
    return res.json();
  },

  async deleteDocument(customerId: string, normaId: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/normas/${normaId}/document`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(`Failed to delete document: ${res.status}`);
  },
};

/** Human-readable size for the attachment line ("2,4 MB"). */
export function formatDocumentSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
}
